import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, MessageSquare, Video, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient } from '~/lib/api';
import { formatDateTime } from '~/lib/utils';

// Component to fetch video and create proper link
function VideoLinkCell({ videoId }: { videoId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['video-info', videoId],
    queryFn: () => apiClient.getVideoById(videoId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return <span className='text-xs text-muted-foreground'>Loading...</span>;
  }

  if (error || !data?.data) {
    return <span className='text-xs text-destructive'>Error</span>;
  }

  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://javfacts.com';
  const videoSlug = data.data.contentId;

  return (
    <a
      href={`${baseUrl}/en/videos/${videoSlug}`}
      target='_blank'
      rel='noopener noreferrer'
      className='flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
      title={`${data.data.dvdId || data.data.title}`}
    >
      View <ExternalLink className='h-3 w-3' />
    </a>
  );
}

export default function ReportsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('all');
  const [type, setType] = useState<string>('all');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reports', page, status, type],
    queryFn: () =>
      apiClient.getReports({
        page,
        limit: 20,
        status: status === 'all' ? undefined : status,
        type: type === 'all' ? undefined : type,
      }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ reportId, newStatus }: { reportId: string; newStatus: 'resolved' | 'dismissed' }) =>
      apiClient.updateReportStatus(reportId, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-reports'] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'resolved':
        return <Badge variant='default'>Resolved</Badge>;
      case 'dismissed':
        return <Badge variant='secondary'>Dismissed</Badge>;
      case 'pending':
        return <Badge variant='destructive'>Pending</Badge>;
      default:
        return <Badge variant='outline'>{status}</Badge>;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'comment':
        return <MessageSquare className='h-4 w-4' />;
      case 'video':
        return <Video className='h-4 w-4' />;
      default:
        return null;
    }
  };

  const getCommentLink = (commentId: string) => {
    // For comments, link to comments tab with filter
    return `/comments?filter=${commentId}`;
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Reports Management</h1>
          <p className='text-muted-foreground'>Handle user reports and moderation requests</p>
        </div>
        <div className='flex items-center gap-2'>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className='w-[150px]'>
              <SelectValue placeholder='Filter by type' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Types</SelectItem>
              <SelectItem value='comment'>Comment</SelectItem>
              <SelectItem value='video'>Video</SelectItem>
              <SelectItem value='user'>User</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className='w-[150px]'>
              <SelectValue placeholder='Filter by status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='pending'>Pending</SelectItem>
              <SelectItem value='resolved'>Resolved</SelectItem>
              <SelectItem value='dismissed'>Dismissed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Reports List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
              <p className='text-muted-foreground'>Loading reports...</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>No reports found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reporter</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.data?.items.map((report: any) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <span className='font-medium'>
                          {report.reporter.username || report.reporter.displayName || 'Anonymous'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {getTypeIcon(report.contentType)}
                          <span className='capitalize'>{report.contentType}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='font-medium capitalize'>{report.reason.replace(/_/g, ' ')}</span>
                      </TableCell>
                      <TableCell>
                        <div className='max-w-[300px] truncate' title={report.description}>
                          {report.description || '-'}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(report.status)}</TableCell>
                      <TableCell>
                        <div className='text-sm text-muted-foreground'>{formatDateTime(report.createdAt)}</div>
                      </TableCell>
                      <TableCell>
                        {report.contentType === 'video' ? (
                          <VideoLinkCell videoId={report.contentId} />
                        ) : report.contentType === 'comment' ? (
                          <a
                            href={getCommentLink(report.contentId)}
                            className='flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                          >
                            View
                          </a>
                        ) : (
                          <span className='text-sm text-muted-foreground'>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {report.status === 'pending' && (
                            <>
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() =>
                                  updateStatusMutation.mutate({ reportId: report.id, newStatus: 'resolved' })
                                }
                                disabled={updateStatusMutation.isPending}
                              >
                                <CheckCircle2 className='h-4 w-4 mr-1 text-green-600' />
                                Resolve
                              </Button>
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() =>
                                  updateStatusMutation.mutate({ reportId: report.id, newStatus: 'dismissed' })
                                }
                                disabled={updateStatusMutation.isPending}
                              >
                                <XCircle className='h-4 w-4 mr-1 text-red-600' />
                                Dismiss
                              </Button>
                            </>
                          )}
                          {report.status !== 'pending' && (
                            <span className='text-sm text-muted-foreground'>
                              {report.status === 'resolved' ? 'Resolved' : 'Dismissed'}
                              {report.reviewedAt && ` • ${formatDateTime(report.reviewedAt)}`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <div className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, data.data.total)} of {data.data.total} reports
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button variant='outline' size='sm' onClick={() => setPage(page - 1)} disabled={page === 1}>
                      Previous
                    </Button>
                    <div className='text-sm'>
                      Page {page} of {data.data.totalPages}
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage(page + 1)}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
